import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Alert, Box, Button, Card, CardContent, Divider, Grid, 
  Paper, Typography, Chip 
} from '@mui/material';
import { useAudience } from '../../hooks/useAudience';
import { useAudiencePreview } from '../../hooks/useAudiencePreview';
import { useTemplates } from '../../hooks/useTemplates';
import { useAutomations } from '../../hooks/useAutomations';
import { useNavigate } from 'react-router-dom';
import formatSchedule from '../../utils/formatSchedule';

const ReviewAutomation = ({ data, onSave, isLoading, showSaveButtons = true }) => {
  // Add debug logging here
  console.log('ReviewAutomation received data:', {
    hasData: Boolean(data),
    hasEmailTemplate: Boolean(data?.emailTemplate),
    emailTemplate: data?.emailTemplate ? {
      id: data.emailTemplate.id,
      hasExportedHtml: Boolean(data.emailTemplate.exportedHtml),
      exportedHtmlLength: data.emailTemplate.exportedHtml?.length,
      htmlContentType: typeof data.emailTemplate.htmlContent,
      htmlContentIsJSON: typeof data.emailTemplate.htmlContent === 'string' && 
                        data.emailTemplate.htmlContent.startsWith('{'),
      htmlContentPreview: data.emailTemplate.htmlContent?.substring(0, 100) + '...',
    } : null
  });
  // DEBUG: Log the full audience data as received
  console.log('[ReviewAutomation] Full data.audience received:', data?.audience);

  const navigate = useNavigate();
  const { createAudience, updateAudience } = useAudience();
  const { createTemplate, updateTemplate } = useTemplates();
  const { createAutomation, updateAutomation } = useAutomations();
  const { 
    isLoading: previewLoading, 
    results: previewResults, 
    previewWithTimeout 
  } = useAudiencePreview();
  
  // Create a more robust safe audience object that won't cause errors
  const safeAudience = useMemo(() => {
    // Debug logging to track incoming audience data
    console.log('[ReviewAutomation] Processing audience data:', data?.audience);
    
    if (!data || !data.audience) {
      return {
        name: 'Default Filter',
        groups: [
          { type: 'ALL', conditions: [] },
          { type: 'ANY', conditions: [] }
        ]
      };
    }
    
    // If we have groups with actual conditions, use them directly
    if (data.audience.groups && Array.isArray(data.audience.groups)) {
      // Validate that conditions exist in the groups
      const allConditionsCount = data.audience.groups.find(g => g.type === 'ALL')?.conditions?.length || 0;
      const anyConditionsCount = data.audience.groups.find(g => g.type === 'ANY')?.conditions?.length || 0;
      
      console.log(`[ReviewAutomation] Found ${allConditionsCount} ALL conditions and ${anyConditionsCount} ANY conditions in direct groups`);
      
      if (allConditionsCount > 0 || anyConditionsCount > 0) {
        // We have valid conditions in one of the groups, so use this structure
        return {
          name: data.audience.filter_name || data.audience.name || 'Audience',
          groups: data.audience.groups
        };
      }
    }
    
    // If no direct groups with conditions, check for match_all/match_any API format
    if (data.audience.match_all || data.audience.match_any) {
      // Convert from API format to our internal format
      const groups = [];
      
      // Process match_all group
      if (data.audience.match_all && data.audience.match_all.length > 0) {
        const allConditions = [];
        data.audience.match_all.forEach(group => {
          if (group.conditions && group.conditions.length > 0) {
            group.conditions.forEach(condition => {
              // Convert field to recordType and fieldName
              const fieldParts = condition.field ? condition.field.split('.') : [];
              if (fieldParts.length === 2) {
                allConditions.push({
                  recordType: fieldParts[0],
                  fieldName: fieldParts[1],
                  operator: condition.operator,
                  value: condition.value,
                  conditionGroup: 'ALL',
                  id: `all-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
                });
              }
            });
          }
        });
        
        console.log(`[ReviewAutomation] Extracted ${allConditions.length} ALL conditions from match_all`);
        
        groups.push({
          type: 'ALL',
          conditions: allConditions
        });
      } else {
        groups.push({ type: 'ALL', conditions: [] });
      }
      
      // Process match_any group
      if (data.audience.match_any && data.audience.match_any.length > 0) {
        const anyConditions = [];
        data.audience.match_any.forEach(group => {
          if (group.conditions && group.conditions.length > 0) {
            group.conditions.forEach(condition => {
              // Convert field to recordType and fieldName
              const fieldParts = condition.field ? condition.field.split('.') : [];
              if (fieldParts.length === 2) {
                anyConditions.push({
                  recordType: fieldParts[0],
                  fieldName: fieldParts[1],
                  operator: condition.operator,
                  value: condition.value,
                  conditionGroup: 'ANY',
                  id: `any-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
                });
              }
            });
          }
        });
        
        console.log(`[ReviewAutomation] Extracted ${anyConditions.length} ANY conditions from match_any`);
        
        groups.push({
          type: 'ANY',
          conditions: anyConditions
        });
      } else {
        groups.push({ type: 'ANY', conditions: [] });
      }
      
      return {
        name: data.audience.filter_name || data.audience.name || 'Audience',
        groups: groups
      };
    }
    
    // Last resort: check if there's a record_type in any conditions
    // This handles an alternative data format that might be present
    try {
      if (data.audience.conditions && Array.isArray(data.audience.conditions)) {
        const allConditions = [];
        const anyConditions = [];
        
        data.audience.conditions.forEach(condition => {
          if (condition.record_type && condition.field_name) {
            const newCondition = {
              recordType: condition.record_type,
              fieldName: condition.field_name,
              operator: condition.operator,
              value: condition.value,
              id: `cond-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
            };
            
            if (condition.condition_group === 'ANY') {
              newCondition.conditionGroup = 'ANY';
              anyConditions.push(newCondition);
            } else {
              newCondition.conditionGroup = 'ALL';
              allConditions.push(newCondition);
            }
          }
        });
        
        console.log(`[ReviewAutomation] Extracted ${allConditions.length} ALL and ${anyConditions.length} ANY conditions from conditions array`);
        
        if (allConditions.length > 0 || anyConditions.length > 0) {
          return {
            name: data.audience.filter_name || data.audience.name || 'Audience',
            groups: [
              { type: 'ALL', conditions: allConditions },
              { type: 'ANY', conditions: anyConditions }
            ]
          };
        }
      }
    } catch (e) {
      console.error('[ReviewAutomation] Error processing audience conditions:', e);
    }
    
    // If we get here, we have audience data but couldn't find valid conditions
    // Return a structure with empty groups
    return {
      name: data.audience.filter_name || data.audience.name || 'Audience',
      groups: [
        { type: 'ALL', conditions: [] },
        { type: 'ANY', conditions: [] }
      ]
    };
  }, [data]);

  // Add logic to determine if we're using a complex filter
  const useComplexFilter = !!data?.audience?.complex_filter_slug;
  
  // Get complex filter name if available
  const complexFilterName = data?.audience?.filter_name || (useComplexFilter ? data?.audience?.complex_filter_slug : null);

  // NEW: Improve formatFieldName function to convert database field names to readable text
  const formatFieldName = (recordType, fieldName) => {
    if (!recordType || !fieldName) return '';
    
    // Convert camelCase or snake_case to Title Case with spaces
    const formatText = (text) => {
      // Handle snake_case first
      const withSpaces = text.replace(/_/g, ' ');
      
      // Then handle camelCase
      const withCamelSpaces = withSpaces.replace(/([A-Z])/g, ' $1');
      
      // Capitalize first letter of each word
      return withCamelSpaces
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
        .trim();
    };
    
    // Format record type for display
    const formattedRecordType = formatText(recordType);
    
    // Format field name for display
    const formattedFieldName = formatText(fieldName);
    
    // For client fields, show "Client: Field Name" format
    return `${formattedRecordType}: ${formattedFieldName}`;
  };

  // Format the display of operator values
  const formatOperator = (operator) => {
    if (!operator) return '';
    
    // Convert from API-style constants to friendly text
    const operatorMap = {
      'EQUALS': 'equals',
      'NOT_EQUALS': 'does not equal',
      'CONTAINS': 'contains',
      'NOT_CONTAINS': 'does not contain',
      'STARTS_WITH': 'starts with',
      'ENDS_WITH': 'ends with',
      'GREATER_THAN': 'is greater than',
      'LESS_THAN': 'is less than',
      'BEFORE': 'is before',
      'AFTER': 'is after',
      'BETWEEN': 'is between',
      'IS_EMPTY': 'is empty',
      'IS_NOT_EMPTY': 'is not empty'
    };
    
    return operatorMap[operator] || operator.toLowerCase();
  };

  // Format the display of values
  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return '';
    }
    
    if (typeof value === 'string') {
      // Format dates in a user-friendly way
      if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return new Date(value).toLocaleDateString();
      }
      return `"${value}"`;
    }
    
    if (Array.isArray(value)) {
      return value.map(v => formatValue(v)).join(', ');
    }
    
    return String(value);
  };
  
  useEffect(() => {
    // Ensure we log the full template data we received for debugging
    console.log("ReviewAutomation received template data:", {
      hasTemplate: !!data?.emailTemplate,
      id: data?.emailTemplate?.id,
      hasHtmlContent: !!data?.emailTemplate?.htmlContent,
      htmlContentLength: data?.emailTemplate?.htmlContent?.length,
      hasExportedHtml: !!data?.emailTemplate?.exportedHtml,
      exportedHtmlLength: data?.emailTemplate?.exportedHtml?.length
    });
    
    // Don't load preview if we don't have audience data
    if (!data?.audience) {
      console.log('No audience data available for preview');
      return;
    }
    
    // Use the new preview hook with timeout
    const loadPreview = async () => {
      console.log('Loading audience preview...');
      await previewWithTimeout(data.audience, 6000); // 6 second timeout
    };
    
    // Add a delay to prevent immediate API calls on component mount
    const timeoutId = setTimeout(() => {
      loadPreview();
    }, 200);
    
    return () => clearTimeout(timeoutId);
  }, [data, previewWithTimeout]);
  
  const handleSave = useCallback(async (activate) => {
    try {
      // Determine if we're editing an existing automation or creating a new one
      const isEditing = Boolean(data.id);
      console.log(`${isEditing ? 'Updating' : 'Creating'} automation${isEditing ? ' ' + data.id : ''}`);

      // STEP 1: Create or update the email template first and get its ID
      console.log('STEP 1: Creating/updating email template first');
      
      // Confirm emailTemplate is available
      if (!data.emailTemplate) {
        throw new Error('Email template data is missing. Please go back and design your email template.');
      }

      // Create a simple template name
      const templateName = `Template_${new Date().getTime()}`;

      // Extract content from the template data - with improved approach for handling design JSON
      let html_content = "";
      let design_json = null;
      let text_content = "";
      
      // First priority: Use designJson if available
      if (data.emailTemplate.designJson) {
        console.log('Using designJson for template state');
        design_json = data.emailTemplate.designJson;
        
        // For htmlContent, prefer the direct HTML content if available
        if (data.emailTemplate.htmlContent) {
          html_content = data.emailTemplate.htmlContent;
          console.log('Using htmlContent for email content');
        } else {
          console.warn('No HTML content found despite having designJson');
          html_content = "<p>Default email content</p>";
        }
      }
      // Second priority: Check for exportedHtml + legacy htmlContent (JSON)
      else if (data.emailTemplate.exportedHtml && data.emailTemplate.htmlContent) {
        console.log('Using exportedHtml for template content and htmlContent as design JSON');
        html_content = data.emailTemplate.exportedHtml;
        // Check if htmlContent looks like JSON
        if (typeof data.emailTemplate.htmlContent === 'string' && 
            data.emailTemplate.htmlContent.trim().startsWith('{')) {
          design_json = data.emailTemplate.htmlContent; // In legacy format, htmlContent stored the JSON design
        }
      }
      // Third priority: Handle old formats
      else if (data.emailTemplate.htmlContent) {
        console.log('Checking htmlContent format');
        if (typeof data.emailTemplate.htmlContent === 'string' && 
            data.emailTemplate.htmlContent.trim().startsWith('{')) {
          try {
            // Try to parse JSON and extract HTML
            design_json = data.emailTemplate.htmlContent; // Store as design JSON
            
            // For HTML content, check if we have exported HTML or extract from the JSON
            if (data.emailTemplate.exportedHtml) {
              html_content = data.emailTemplate.exportedHtml;
            } else {
              // Try to parse and extract HTML
              const parsed = JSON.parse(data.emailTemplate.htmlContent);
              if (parsed.design?.html) {
                html_content = parsed.design.html;
                console.log('Extracted HTML from design.html');
              } else if (parsed.html) {
                html_content = parsed.html;
                console.log('Extracted HTML from html property');
              } else {
                // Fallback to raw content
                html_content = "<p>Email content unavailable</p>";
              }
            }
          } catch (e) {
            console.error('Error parsing htmlContent:', e);
            html_content = data.emailTemplate.htmlContent;
          }
        } else {
          // If htmlContent is not JSON, treat it as actual HTML
          html_content = data.emailTemplate.htmlContent;
        }
      } else {
        console.warn('No HTML content found in email template, using default');
        html_content = "<p>Default email content</p>";
      }
      
      // Extract text content
      text_content = data.emailTemplate.textContent || 
                     "Default plain text email content";
      
      // Prepare template payload with new design_json field
      const templatePayload = {
        name: templateName,
        html_content: html_content,
        text_content: text_content,
        design_json: design_json,
        automation_id: null // We'll update this later once we have the automation ID
      };
      
      // CRITICAL: Make sure we have some design_json content even if it's empty
      // This ensures the template can be edited later, even if Save Template wasn't clicked
      if (!templatePayload.design_json && templatePayload.html_content) {
        console.log('⚠️ WARNING: No design_json found, generating fallback design from HTML');
        try {
          // Create a minimal Unlayer design structure with the HTML content
          const fallbackDesign = {
            body: {
              rows: [{
                cells: [1],
                columns: [{
                  contents: [{
                    type: "text",
                    values: {
                      html: templatePayload.html_content
                    }
                  }]
                }]
              }]
            }
          };
          
          templatePayload.design_json = JSON.stringify(fallbackDesign);
          console.log('Generated fallback design_json:', templatePayload.design_json.length, 'chars');
        } catch (e) {
          console.error('Failed to generate fallback design:', e);
        }
      }
      
      console.log('Template payload prepared:', {
        name: templateName,
        html_content_length: html_content.length,
        design_json_length: design_json ? design_json.length : 0,
        text_content_length: text_content.length
      });
      
      // Create or update template
      let templateId;
      if (isEditing && data.emailTemplate.id) {
        // Update existing template
        console.log('Updating existing template:', data.emailTemplate.id);
        const updatedTemplate = await updateTemplate(data.emailTemplate.id, templatePayload);
        templateId = data.emailTemplate.id;
        console.log('Template updated successfully:', updatedTemplate);
      } else {
        // Create new template
        console.log('Creating new template with name:', templateName);
        const createdTemplate = await createTemplate(templatePayload);
        console.log('Template created successfully:', createdTemplate);
        templateId = createdTemplate?.id;
      }

      if (!templateId) {
        console.error('Failed to get template ID after creation/update');
        throw new Error('Failed to get template ID');
      }
      
      console.log('✅ Successfully obtained template_id:', templateId);
      
      // STEP 2: Now create the automation using the template ID
      console.log('STEP 2: Creating automation with template_id:', templateId);
      
      // Use audienceId directly from data
      const audienceId = data.audienceId;
      if (!audienceId && !data.audience?.complex_filter_slug) {
        console.error('Error: Both audienceId and complex_filter_slug are missing');
        throw new Error('Audience selection is required. Please go back and select or create an audience.');
      }

      // Create the automation payload
      const payload = {
        title: data.title || "Untitled Automation",
        email_subject: data.emailSubject || "No Subject",
        email_sender_id: data.emailFromId,
        schedule_type: data.scheduleType || "DAILY",
        template_id: templateId, // Use the template ID we just got
        status: activate ? 'ACTIVE' : 'DRAFT', // Make sure status is set
        schedule_interval: Number(data.scheduleInterval || 1),
      };
      
      // CRITICAL: Make absolutely sure the status is set for the backend schema
      console.log(`Setting status to: ${payload.status}`);
      
      // Add audienceId if available
      if (audienceId) {
        payload.audience_id = audienceId;
      }
      
      // If we have a complex filter, add it to the payload
      if (data.audience && data.audience.complex_filter_slug) {
        payload.complex_filter_slug = data.audience.complex_filter_slug;
        console.log(`Using complex filter: ${payload.complex_filter_slug}`);
      }

      // Determine schedule fields based on schedule type
      if (data.scheduleType === 'DAILY') {
        payload.schedule_time = data.scheduleTime || "09:00";
        delete payload.schedule_day;
        delete payload.schedule_days;
      } else if (data.scheduleType === 'WEEKLY') {
        payload.schedule_time = data.scheduleTime || "09:00";
        payload.schedule_days = data.scheduleDays || [];
        delete payload.schedule_day;
      } else if (data.scheduleType === 'MONTHLY') {
        payload.schedule_time = data.scheduleTime || "09:00";
        payload.schedule_day = Number(data.scheduleDay || 1);
        delete payload.schedule_days;
      } else {
        delete payload.schedule_time;
        delete payload.schedule_day;
        delete payload.schedule_days;
      }

      // Check for any undefined values
      const undefinedFields = Object.entries(payload)
        .filter(([_, value]) => value === undefined)
        .map(([key]) => key);

      if (undefinedFields.length > 0) {
        console.warn('Warning: The following fields are undefined:', undefinedFields);
      }

      console.log('Final automation payload:', JSON.stringify(payload, null, 2));

      // Create or update the automation
      let result;
      if (isEditing) {
        // Update existing automation
        result = await updateAutomation(data.id, payload);
        console.log('Automation updated successfully:', result);
      } else {
        // Create new automation
        result = await createAutomation(payload);
        console.log('Automation created successfully:', result);
      }

      if (onSave) onSave();
      navigate('/automations');
    } catch (error) {
      console.error('Error in overall save process:', error);

      // Try to get detailed error information
      if (error.response) {
        const { status, data } = error.response;
        console.error(`Error ${status}:`, data);

        // If we have validation errors, show them more clearly
        if (status === 400 || status === 422) {
          if (data.detail) {
            console.error('Validation errors:', data.detail);
          }
        }
      }
      
      // Show user-friendly error message
      let errorMessage = 'Failed to save automation. ';
      
      if (error.message) {
        errorMessage += error.message;
      } else if (error.response?.status === 422) {
        errorMessage += 'There were validation errors. Please check your inputs and try again.';
      } else if (error.response?.status === 500) {
        errorMessage += 'There was a server error. Please try again in a few minutes.';
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        errorMessage += 'The request timed out. Please check your connection and try again.';
      } else {
        errorMessage += 'Please check your inputs and try again.';
      }
      
      // Here you might want to show this error to the user via a notification system
      // For now, we'll just log it - you can add a toast/alert here
      alert(errorMessage); // Temporary - replace with proper UI notification
    }
  }, [data, safeAudience, onSave, createTemplate, updateTemplate, createAutomation, updateAutomation, navigate]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Review Your Automation
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        Please review all details before saving. You can go back to previous steps to make changes.
      </Alert>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>General Settings</Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Automation Title</Typography>
              <Typography>{data?.title || 'Untitled Automation'}</Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Email Subject</Typography>
              <Typography>{data?.emailSubject || 'No subject'}</Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Schedule</Typography>
              <Typography>{formatSchedule(data)}</Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>Audience</Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Filter Name</Typography>
              <Typography>
                {useComplexFilter ? (
                  <>
                    {safeAudience.name || complexFilterName || 'Complex Filter'}
                    <Chip 
                      size="small" 
                      label="Complex Filter" 
                      color="primary" 
                      sx={{ ml: 1, fontSize: '0.7rem' }} 
                    />
                  </>
                ) : (
                  safeAudience.name || 'Default Filter'
                )}
              </Typography>
            </Box>
            
            {/* Only show conditions if NOT using a complex filter */}
            {!useComplexFilter && (
              <>
                {/* UPDATED Required Conditions (ALL) section */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Required Conditions (ALL)</Typography>
                  {(safeAudience.groups?.find(g => g.type === 'ALL')?.conditions ?? []).length > 0 ? (
                    <ul>
                      {(safeAudience.groups.find(g => g.type === 'ALL')?.conditions ?? []).map((condition, index) => (
                        <li key={`all-${index}`}>
                          {formatFieldName(condition.recordType, condition.fieldName)} {formatOperator(condition.operator)} {formatValue(condition.value)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Typography color="error">No required conditions defined</Typography>
                  )}
                </Box>
                
                {/* UPDATED Optional Conditions (ANY) section */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Optional Conditions (ANY)</Typography>
                  {(safeAudience.groups?.find(g => g.type === 'ANY')?.conditions ?? []).length > 0 ? (
                    <ul>
                      {(safeAudience.groups.find(g => g.type === 'ANY')?.conditions ?? []).map((condition, index) => (
                        <li key={`any-${index}`}>
                          {formatFieldName(condition.recordType, condition.fieldName)} {formatOperator(condition.operator)} {formatValue(condition.value)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Typography color="text.secondary">No optional conditions defined</Typography>
                  )}
                </Box>
              </>
            )}
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Estimated Recipients</Typography>
              {previewLoading ? (
                <Typography>Loading preview...</Typography>
              ) : previewResults ? (
                <>
                  <Typography>
                    {previewResults.matched_count || 0} clients match your criteria
                  </Typography>
                  {/* Show error message if one exists */}
                  {previewResults.error && (
                    <Alert severity={previewResults.error.includes('will not affect') ? 'info' : 'warning'} sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        {previewResults.error}
                      </Typography>
                    </Alert>
                  )}
                  {previewResults.sample_clients && previewResults.sample_clients.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>Sample clients:</Typography>
                      <Grid container spacing={1}>
                        {previewResults.sample_clients.slice(0, 3).map((client, index) => (
                          <Grid item xs={12} sm={4} key={client.id || index}>
                            <Card variant="outlined" sx={{ p: 1 }}>
                              <Typography variant="body2" fontWeight="bold">{client.name}</Typography>
                              {client.email && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {client.email}
                                </Typography>
                              )}
                              {client.service && (
                                <Typography variant="caption" color="primary" display="block">
                                  Service: {client.service}
                                </Typography>
                              )}
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </>
              ) : (
                <Typography>No preview available</Typography>
              )}
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Email Preview</Typography>
            
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2">Subject</Typography>
                <Typography>{data?.emailSubject || 'No subject'}</Typography>
              </CardContent>
            </Card>
            
            <Box sx={{ 
              border: '1px solid #ccc', 
              p: 2, 
              height: '500px', 
              overflowY: 'auto',
              backgroundColor: '#fff'
            }}>
              {/* Debug information */}
              {console.log('Rendering email preview with:', {
                hasExportedHtml: Boolean(data?.emailTemplate?.exportedHtml),
                hasHtmlContent: Boolean(data?.emailTemplate?.htmlContent),
                hasDesignJson: Boolean(data?.emailTemplate?.designJson),
                contentType: typeof data?.emailTemplate?.htmlContent,
                emailTemplate: data?.emailTemplate
              })}

              {/* Try to properly render the email template */}
              {(() => {
                // Check if we have any content to display
                if (!data?.emailTemplate) {
                  return <Box component="div">No email template available.</Box>;
                }
                
                // First priority: Use direct HTML content
                if (data.emailTemplate.htmlContent && 
                    typeof data.emailTemplate.htmlContent === 'string' && 
                    !data.emailTemplate.htmlContent.startsWith('{') &&
                    !data.emailTemplate.htmlContent.startsWith('[')) {
                  console.log("Rendering direct HTML content");
                  return <div dangerouslySetInnerHTML={{ __html: data.emailTemplate.htmlContent }} />;
                }
                
                // Second priority: Use exportedHtml if available
                if (data.emailTemplate.exportedHtml) {
                  console.log("Rendering from exportedHtml");
                  return <div dangerouslySetInnerHTML={{ __html: data.emailTemplate.exportedHtml }} />;
                }

                // Third priority: Try to extract HTML from designJson
                if (data.emailTemplate.designJson) {
                  try {
                    const parsed = JSON.parse(data.emailTemplate.designJson);
                    // Look for HTML in different possible locations
                    if (parsed.html) {
                      return <div dangerouslySetInnerHTML={{ __html: parsed.html }} />;
                    }
                    if (parsed.body?.rows?.[0]?.columns?.[0]?.contents?.[0]?.values?.html) {
                      return <div dangerouslySetInnerHTML={{ 
                        __html: parsed.body.rows[0].columns[0].contents[0].values.html 
                      }} />;
                    }
                  } catch (e) {
                    console.error("Error parsing designJson:", e);
                  }
                }

                // Fourth priority: Try to extract HTML from htmlContent as JSON
                if (data.emailTemplate.htmlContent && 
                    (data.emailTemplate.htmlContent.startsWith('{') || 
                     data.emailTemplate.htmlContent.startsWith('['))) {
                  try {
                    const parsed = JSON.parse(data.emailTemplate.htmlContent);
                    
                    // Try all common locations for HTML content
                    if (parsed.design?.html) return <div dangerouslySetInnerHTML={{ __html: parsed.design.html }} />;
                    if (parsed.html) return <div dangerouslySetInnerHTML={{ __html: parsed.html }} />;
                    if (parsed.body?.rows?.[0]?.columns?.[0]?.contents?.[0]?.values?.html) {
                      return <div dangerouslySetInnerHTML={{ 
                        __html: parsed.body.rows[0].columns[0].contents[0].values.html 
                      }} />;
                    }
                    
                    // If we couldn't find HTML in the structure
                    return <Box component="div">Template structure found but HTML content couldn't be extracted.</Box>;
                  } catch (e) {
                    console.error('Error parsing JSON design:', e);
                  }
                }

                // No content available
                return <Box component="div">No email content available.</Box>;
              })()}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Save Buttons - only show if explicitly requested (for standalone use) */}
      {showSaveButtons && (
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => handleSave(false)}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save as Draft'}
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={() => handleSave(true)}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save & Activate'}
          </Button>
        </Box>
      )}
      
    </Box>
  );
};

export default ReviewAutomation;